package com.evoapps.evoshift.viewmodels

import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import com.evoapps.evoshift.domain.model.defaultWorkplaceId
import kotlinx.coroutines.flow.catch
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.emptyFlow
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.map
import kotlinx.coroutines.flow.onEach
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.ExperimentalCoroutinesApi
import com.evoapps.evoshift.ui.util.toUiText
import com.evoapps.evoshift.ui.util.toNote
import com.evoapps.evoshift.location.LocationReader
import com.evoapps.evoshift.domain.usecase.shift.ClockStatus
import com.evoapps.evoshift.ui.util.UiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.error_user_not_found
import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.Roster
import com.evoapps.evoshift.domain.usecase.EmployeeDashboardState
import com.evoapps.evoshift.domain.usecase.GetEmployeeDashboardUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.domain.usecase.roster.GetRosterSummaryUseCase
import com.evoapps.evoshift.session.WorkplaceSessionManager
import com.evoapps.evoshift.session.WorkplaceMonthSelection
import com.evoapps.evoshift.session.of
import com.evoapps.evoshift.domain.model.RosterIds
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toLocalDateTime
import com.evoapps.evoshift.domain.util.AppClock.now

data class EmployeeDashboardUiState(
    val isLoading: Boolean = true,
    val userName: String = "",
    val workplaceName: String = "",
    val dashboardData: EmployeeDashboardState? = null,
    /** The workplace's currency, for the earnings. */
    val currency: String = DEFAULT_CURRENCY,
    /** What the clock-in button does now. */
    val clock: ClockStatus = ClockStatus.Idle,
    val clockBusy: Boolean = false,
    /** SH-106: the clock worked but the manager will see it flagged (outside the area, no location). */
    val clockNote: UiText? = null,
    /** SH-106: the one-time explanation of the location reading is open. */
    val locationNotice: Boolean = false,
    /** SH-165: clocking out well before the planned end ("HH:mm") waits for the person's answer. */
    val earlyClockOut: String? = null,
    /** SH-165: the clock-out just done, offered for undo in the snackbar. */
    val clockOutUndo: ClockOutUndo? = null,
    val error: UiText? = null
)

class EmployeeDashboardViewModel(
    private val getCurrentUserUseCase: GetCurrentUserUseCase,
    private val getRosterSummaryUseCase: GetRosterSummaryUseCase,
    private val getEmployeeDashboardUseCase: GetEmployeeDashboardUseCase,
    private val sessionManager: WorkplaceSessionManager,
    private val myShiftClock: MyShiftClock,
    /** For the workplace's currency; without it amounts show in PLN. */
    private val workplaceRepository: WorkplaceRepository? = null,
    /** SH-89: the top bar's month, for "My summary"; the next shift stays the next one. */
    private val monthSelection: WorkplaceMonthSelection? = null
) : ViewModel() {

    private val _uiState = MutableStateFlow(EmployeeDashboardUiState())
    val uiState = _uiState.asStateFlow()

    private val clockResultChannel = kotlinx.coroutines.channels.Channel<Boolean>(kotlinx.coroutines.channels.Channel.BUFFERED)

    /** SH-158: each clock, break or "without a shift" tap's outcome, once (true: done), for the screen's haptics. */
    val clockResults: kotlinx.coroutines.flow.Flow<Boolean> = clockResultChannel.receiveAsFlow()

    init {
        observeSession()
        observeClock()
    }

    @OptIn(ExperimentalCoroutinesApi::class)
    private fun observeClock() {
        viewModelScope.launch {
            // Profile updates of the same person keep the clock listener.
            getCurrentUserUseCase().map { it?.uid }.distinctUntilChanged().flatMapLatest { uid ->
                if (uid == null) flowOf(MyShiftClock.State()) else myShiftClock.observe(uid)
            }.catch { e -> _uiState.update { it.copy(error = e.toUiText()) } } // SH-188
                .collect { state -> _uiState.update { it.copy(clock = state.clock) } }
        }
    }

    /** The location reader of the screen (SH-106), kept while the one-time notice is open. */
    private var pendingLocationReader: LocationReader? = null

    /**
     * The clock button. Where the workplace checks the location (SH-106), the first tap on this
     * device explains why first (the locationNotice state); [readLocation] is then called once.
     */
    fun onClockClicked(readLocation: LocationReader? = null) = clockOrBreak(readLocation, breakButton = false)

    /** SH-116: the break button while working: starts a break, or ends the one going on. */
    fun onBreakClicked(readLocation: LocationReader? = null) = clockOrBreak(readLocation, breakButton = true)

    /** Which button waits for the location notice (SH-116: the clock or the break). */
    private var pendingBreak = false
    /** SH-134: the location notice was opened by "clock in without a scheduled shift". */
    private var pendingWithoutShift = false

    /**
     * SH-134: "Clock in without a scheduled shift" (covering for someone, called in): the server
     * records it on a colleague's shift or a new one, both for a manager to confirm.
     */
    fun onClockWithoutShift(readLocation: LocationReader? = null) {
        val workplaceId = sessionManager.currentWorkplaceId.value ?: return
        if (_uiState.value.clockBusy) return
        viewModelScope.launch {
            if (myShiftClock.needsLocationNoticeAt(workplaceId)) {
                pendingLocationReader = readLocation
                pendingWithoutShift = true
                _uiState.update { it.copy(locationNotice = true) }
                return@launch
            }
            clockWithoutShift(workplaceId, readLocation)
        }
    }

    private suspend fun clockWithoutShift(workplaceId: String, readLocation: LocationReader?) {
        _uiState.update { it.copy(clockBusy = true, clockNote = null) }
        val result = myShiftClock.clockInWithoutShift(workplaceId, readLocation)
        _uiState.update {
            it.copy(clockBusy = false, error = result.exceptionOrNull()?.toUiText(), clockNote = result.getOrNull()?.toNote())
        }
        clockResultChannel.trySend(result.isSuccess)
    }

    /** The location reader kept while the early clock-out question is open (SH-165). */
    private var earlyClockOutReader: LocationReader? = null

    private fun clockOrBreak(readLocation: LocationReader?, breakButton: Boolean) {
        val status = _uiState.value.clock
        // SH-165: a clock-out well before the planned end is asked about first.
        if (!breakButton && status is ClockStatus.Working && myShiftClock.isEarlyClockOut(status)) {
            earlyClockOutReader = readLocation
            _uiState.update { it.copy(earlyClockOut = clockTimeOf(status.shift.plannedTime.end)) }
            return
        }
        proceed(status, readLocation, breakButton)
    }

    /** SH-165: the answer to "Planned end 22:00. Clock out now?" ([confirmed]: clock out). */
    fun onEarlyClockOutAnswered(confirmed: Boolean) {
        val reader = earlyClockOutReader
        earlyClockOutReader = null
        _uiState.update { it.copy(earlyClockOut = null) }
        if (confirmed) proceed(_uiState.value.clock, reader, breakButton = false)
    }

    /** SH-165: Undo in the "Clocked out" snackbar: the shift is open again. */
    fun onUndoClockOut() {
        val undo = _uiState.value.clockOutUndo ?: return
        _uiState.update { it.copy(clockOutUndo = null) }
        viewModelScope.launch {
            val uid = getCurrentUserUseCase().first()?.uid ?: return@launch
            _uiState.update { it.copy(clockBusy = true) }
            val result = myShiftClock.undoClockOut(undo.shiftId, uid)
            _uiState.update { it.copy(clockBusy = false, error = result.exceptionOrNull()?.toUiText()) }
            clockResultChannel.trySend(result.isSuccess)
        }
    }

    /** SH-165: the "Clocked out" snackbar is gone without Undo. */
    fun dismissClockOutUndo() = _uiState.update { it.copy(clockOutUndo = null) }

    private fun proceed(status: ClockStatus, readLocation: LocationReader?, breakButton: Boolean) {
        viewModelScope.launch {
            if (myShiftClock.needsLocationNotice(status)) {
                pendingLocationReader = readLocation
                pendingBreak = breakButton
                _uiState.update { it.copy(locationNotice = true) }
                return@launch
            }
            clock(status, readLocation, breakButton)
        }
    }

    /** The person read the location notice ([accepted]) or closed it (nothing is clocked). */
    fun onLocationNoticeAnswered(accepted: Boolean) {
        val reader = pendingLocationReader
        pendingLocationReader = null
        _uiState.update { it.copy(locationNotice = false) }
        val withoutShift = pendingWithoutShift
        pendingWithoutShift = false
        if (!accepted) return
        myShiftClock.acknowledgeLocationNotice()
        if (withoutShift) {
            val workplaceId = sessionManager.currentWorkplaceId.value ?: return
            viewModelScope.launch { clockWithoutShift(workplaceId, reader) }
            return
        }
        val status = _uiState.value.clock
        val breakButton = pendingBreak
        viewModelScope.launch { clock(status, reader, breakButton) }
    }

    private suspend fun clock(status: ClockStatus, readLocation: LocationReader?, breakButton: Boolean = false) {
        val uid = getCurrentUserUseCase().first()?.uid ?: return
        _uiState.update { it.copy(clockBusy = true, clockNote = null) }
        val result = myShiftClock.click(status, uid, readLocation, breakButton)
        val clockedOut = result.isSuccess && !breakButton && status is ClockStatus.Working
        _uiState.update {
            it.copy(
                clockBusy = false, error = result.exceptionOrNull()?.toUiText(), clockNote = result.getOrNull()?.toNote(),
                clockOutUndo = if (clockedOut) ClockOutUndo((status as ClockStatus.Working).shift.id) else it.clockOutUndo
            )
        }
        clockResultChannel.trySend(result.isSuccess)
    }

    fun clearClockNote() = _uiState.update { it.copy(clockNote = null) }

    fun clearError() {
        _uiState.update { it.copy(error = null) }
    }

    private fun observeSession() {
        viewModelScope.launch {
            sessionManager.currentWorkplaceId.collectLatest { workplaceId ->
                if (workplaceId != null) {
                    loadData(workplaceId)
                } else {
                    loadInitial()
                }
            }
        }
    }

    /** Picks the first workplace; runs until the session has one (collectLatest then cancels it). */
    private suspend fun loadInitial() {
        getCurrentUserUseCase().collectLatest { user ->
            val firstId = user?.defaultWorkplaceId()
            if (user != null && firstId != null) {
                // Don't overwrite if it's already set to a valid workspace
                if (sessionManager.currentWorkplaceId.value == null) {
                    sessionManager.setWorkplaceId(firstId)
                }
            } else {
                _uiState.update { it.copy(isLoading = false) }
            }
        }
    }

    /**
     * The person's dashboard for the current roster of [workplaceId], live. The previous content
     * stays until the new arrives; profile updates (same person) and roster list updates that keep
     * the same current roster do not restart the dashboard listener.
     */
    @OptIn(ExperimentalCoroutinesApi::class)
    private suspend fun loadData(workplaceId: String) {
        _uiState.update { it.copy(isLoading = it.dashboardData == null) }
        val currency = workplaceRepository.currencyOf(workplaceId)
        _uiState.update { it.copy(currency = currency) }

        getCurrentUserUseCase()
            .onEach { user ->
                if (user == null) {
                    _uiState.update { it.copy(isLoading = false, error = UiText.Resource(Res.string.error_user_not_found)) }
                } else {
                    val engagement = user.engagements[workplaceId]
                    val workplaceName = engagement?.cachedWorkplaceName?.takeIf { it != "TBD" && it.isNotBlank() } .orEmpty()
                    _uiState.update { it.copy(userName = user.displayName, workplaceName = workplaceName) }
                }
            }
            .map { it?.uid }
            .distinctUntilChanged()
            .flatMapLatest { uid ->
                if (uid == null) emptyFlow<EmployeeDashboardState?>()
                else getRosterSummaryUseCase(workplaceId)
                    .let { rosters ->
                        val month = monthSelection?.month
                        if (month == null) rosters.map { currentRosterId(it) }
                        // A month without a roster has nothing to sum (its id reads as empty).
                        else combine(rosters, month) { list, m -> list.of(m)?.id ?: RosterIds.of(workplaceId, m.year, m.month.number) }
                    }
                    .distinctUntilChanged()
                    .flatMapLatest { rosterId ->
                        if (rosterId == null) flowOf(null) else getEmployeeDashboardUseCase.invoke(uid, rosterId)
                    }
            }
            // SH-188: a failed listener leaves the screen with a message, not an ended coroutine.
            .catch { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
            .collect { data ->
                _uiState.update {
                    it.copy(
                        isLoading = false,
                        dashboardData = data
                    )
                }
            }
    }

    /** This month's roster, else the nearest upcoming one, else the latest. */
    private fun currentRosterId(rosters: List<Roster>): String? {
        val now = now().toLocalDateTime(TimeZone.currentSystemDefault())
        val currentMonth = now.month.number
        val currentYear = now.year
        val targetRoster =
            rosters.find { it.month == currentMonth && it.year == currentYear }
                ?: rosters.filter { it.year > currentYear || (it.year == currentYear && it.month > currentMonth) }
                    .minByOrNull { it.year * 100 + it.month }
                ?: rosters.maxByOrNull { it.year * 100 + it.month }
        return targetRoster?.id
    }
}
